// ===== Packages =====

import {
    Descendant,
    Text as SlateText,
}                               from 'slate';
import { v4 as uuidv4 }         from 'uuid';

// ===== Elements =====

import { HEADER_SIZE }          from '../components/Editor/elements/enums';
import BlockQuoteElement        from '../components/Editor/elements/blockQuoteElement';
import HeaderElement            from '../components/Editor/elements/headerElement';
import LineElement              from '../components/Editor/elements/lineElement';
import {
    ListElement,
    ListItemElement,
}                               from '../components/Editor/elements/listElement';
import { LIST_TYPE }            from '../components/Editor/elements/listElement/enums';
import {
    CustomText,
    HeaderNode,
    LineNode,
    BlockQuoteNode,
    ListNode,
    ListItemNode,
    WebLinkNode,
}                               from '../components/Editor/interfaces';
import { EMPHASIZER_MARK }      from '../components/Editor/plugins/enums';

const COLLAPSIBLE_INLINE_WHITESPACE = /[^\S\n]+/g;
const BLOCK_TAG_NAMES = new Set([
    'article',
    'aside',
    'blockquote',
    'div',
    'figcaption',
    'footer',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'li',
    'main',
    'ol',
    'p',
    'section',
    'ul',
]);
const INLINE_BLOCK_BOUNDARY_TAG_NAMES = new Set([
    'div',
    'p',
    'section',
    'article',
    'header',
    'footer',
    'aside',
    'main',
]);
const HEADER_ONE_MIN_FONT_SIZE_PX = 25;
const HEADER_TWO_MIN_FONT_SIZE_PX = 19;
const CLIPBOARD_PARSING_ROOT_ATTRIBUTE = 'data-readiview-clipboard-root';
const FONT_SIZE_COMPARISON_EPSILON_PX = 0.75;
const MIN_RELATIVE_HEADING_FONT_SIZE_PX = 16;

interface IDeserializeSlateClipboardHtmlArgs {
    html: string,
}

interface IClipboardMarks {
    [EMPHASIZER_MARK.bold]?: boolean,
    [EMPHASIZER_MARK.italicize]?: boolean,
    [EMPHASIZER_MARK.underline]?: boolean,
    [EMPHASIZER_MARK.strikethrough]?: boolean,
}

interface IClipboardLineBreakToken {
    kind: 'line-break',
}

interface IDeserializeInlineNodeArgs {
    node: ChildNode,
    activeMarks: IClipboardMarks,
}

interface IDeserializeInlineChildrenArgs {
    parentNode: ParentNode,
    activeMarks: IClipboardMarks,
}

interface IBuildLineLikeBlocksArgs {
    element: HTMLElement,
    activeMarks: IClipboardMarks,
    buildBlock: (children: Descendant[]) => Descendant,
    allowEmptyBreaks?: boolean,
}

interface IBuildBlocksFromInlineNodesArgs {
    inlineNodes: Array<Descendant | IClipboardLineBreakToken>,
    buildBlock: (children: Descendant[]) => Descendant,
    allowEmptyBreaks?: boolean,
}

const LINE_BREAK_TOKEN: IClipboardLineBreakToken = {
    kind: 'line-break',
};

const isElementNode = (node: ChildNode): node is HTMLElement => node.nodeType === Node.ELEMENT_NODE;

const isTextNode = (node: ChildNode): node is globalThis.Text => node.nodeType === Node.TEXT_NODE;

const isLineBreakToken = (
    value: Descendant | IClipboardLineBreakToken,
): value is IClipboardLineBreakToken => (
    'kind' in value
    && value.kind === LINE_BREAK_TOKEN.kind
);

/**
 * Collapses browser formatting whitespace while keeping literal newline
 * characters available for conversion into explicit Slate line breaks.
 */
function normalizeTextLineSegment(value: string): string {
    return value
        .replace(/\u00A0/g, ' ')
        .replace(COLLAPSIBLE_INLINE_WHITESPACE, ' ');
}

function normalizeTextNewlines(value: string): string {
    return value
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');
}

function segmentContainsVisibleText(value: string): boolean {
    return normalizeTextLineSegment(value).trim().length > 0;
}

function shouldIgnoreElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    return tagName === 'meta'
        || tagName === 'script'
        || tagName === 'style';
}

function nodeOnlyRepresentsSpacing(node: ChildNode): boolean {
    if (isTextNode(node)) {
        return normalizeTextLineSegment(
            normalizeTextNewlines(node.textContent || ''),
        ).trim().length === 0;
    }

    if (!isElementNode(node)) {
        return true;
    }

    if (shouldIgnoreElement(node) || node.tagName.toLowerCase() === 'br') {
        return true;
    }

    const childContainsNonTextMedia = !!node.querySelector(
        'audio, canvas, iframe, img, object, picture, svg, table, video',
    );
    if (childContainsNonTextMedia) {
        return false;
    }

    const normalizedNodeText = normalizeTextLineSegment(
        normalizeTextNewlines(node.textContent || ''),
    ).trim();
    if (normalizedNodeText.length > 0) {
        return false;
    }

    return Array.from(node.childNodes).every((childNode) => nodeOnlyRepresentsSpacing(childNode));
}

function getComputedStyleSafe(element: HTMLElement): CSSStyleDeclaration | null {
    if (
        typeof window === 'undefined'
        || typeof window.getComputedStyle !== 'function'
    ) {
        return null;
    }

    try {
        return window.getComputedStyle(element);
    } catch (error) {
        return null;
    }
}

function getResolvedStyleValue({
    element,
    inlineValue,
    pickComputedValue,
}: {
    element: HTMLElement,
    inlineValue: string,
    pickComputedValue: (style: CSSStyleDeclaration) => string,
}): string {
    const trimmedInlineValue = inlineValue.trim();
    if (trimmedInlineValue.length > 0) {
        return trimmedInlineValue;
    }

    const computedStyle = getComputedStyleSafe(element);
    if (!computedStyle) {
        return '';
    }

    return pickComputedValue(computedStyle).trim();
}

function elementHasInlineUnderline(element: HTMLElement): boolean {
    const textDecoration = `${element.style.textDecorationLine} ${element.style.textDecoration}`
        .toLowerCase()
        .trim();

    return textDecoration.includes('underline');
}

/**
 * Browser default styles underline anchors. That visual affordance should stay
 * attached to the web-link element instead of becoming a duplicated Slate
 * underline mark on every pasted link leaf.
 */
function shouldIgnoreImplicitLinkUnderline({
    element,
    textDecoration,
}: {
    element: HTMLElement,
    textDecoration: string,
}): boolean {
    if (!textDecoration.includes('underline')) {
        return false;
    }

    const closestLink = element.closest('a');
    if (!closestLink) {
        return false;
    }

    let currentElement: HTMLElement | null = element;
    while (currentElement) {
        if (elementHasInlineUnderline(currentElement)) {
            return false;
        }

        if (currentElement === closestLink) {
            return true;
        }

        currentElement = currentElement.parentElement;
    }

    return false;
}

function isBlockElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    if (BLOCK_TAG_NAMES.has(tagName)) {
        return true;
    }

    const display = getResolvedStyleValue({
        element,
        inlineValue: element.style.display,
        pickComputedValue: (style) => style.display,
    }).toLowerCase();
    return display === 'block' || display === 'list-item';
}

function parseFontWeight(value: string): number | null {
    const normalizedValue = value.toLowerCase().trim();
    if (!normalizedValue) {
        return null;
    }

    if (normalizedValue === 'bold' || normalizedValue === 'bolder') {
        return 700;
    }

    const parsedValue = Number(normalizedValue);
    if (!Number.isNaN(parsedValue)) {
        return parsedValue;
    }

    return null;
}

function parseFontSizePx(value: string): number | null {
    const normalizedValue = value.toLowerCase().trim();
    if (!normalizedValue) {
        return null;
    }

    const parsedValue = Number.parseFloat(normalizedValue);
    if (Number.isNaN(parsedValue)) {
        return null;
    }

    if (normalizedValue.endsWith('px')) {
        return parsedValue;
    }

    if (normalizedValue.endsWith('pt')) {
        return parsedValue * (96 / 72);
    }

    if (normalizedValue.endsWith('rem') || normalizedValue.endsWith('em')) {
        return parsedValue * 16;
    }

    return parsedValue;
}

function elementContainsVisibleText(element: HTMLElement): boolean {
    return !!element.textContent?.trim();
}

function getHeadingStyleTargets(element: HTMLElement): HTMLElement[] {
    return [
        element,
        ...Array.from(element.querySelectorAll<HTMLElement>('*')),
    ].filter((target) => elementContainsVisibleText(target));
}

function resolveElementMaxFontSizePx(element: HTMLElement): number {
    return getHeadingStyleTargets(element).reduce<number>((currentMax, target) => {
        const fontSize = getResolvedStyleValue({
            element: target,
            inlineValue: target.style.fontSize,
            pickComputedValue: (style) => style.fontSize,
        });
        const fontSizePx = parseFontSizePx(fontSize);
        if (fontSizePx === null) {
            return currentMax;
        }

        return Math.max(currentMax, fontSizePx);
    }, 0);
}

function resolveElementMaxFontWeight(element: HTMLElement): number {
    return getHeadingStyleTargets(element).reduce<number>((currentMax, target) => {
        const fontWeight = getResolvedStyleValue({
            element: target,
            inlineValue: target.style.fontWeight,
            pickComputedValue: (style) => style.fontWeight,
        });
        const numericFontWeight = parseFontWeight(fontWeight);
        if (numericFontWeight === null) {
            return currentMax;
        }

        return Math.max(currentMax, numericFontWeight);
    }, 0);
}

function fontSizesApproximatelyMatch(firstSizePx: number, secondSizePx: number): boolean {
    return Math.abs(firstSizePx - secondSizePx) <= FONT_SIZE_COMPARISON_EPSILON_PX;
}

function getClipboardParsingRoot(element: HTMLElement): HTMLElement | null {
    let currentElement: HTMLElement | null = element;
    while (currentElement) {
        if (currentElement.hasAttribute(CLIPBOARD_PARSING_ROOT_ATTRIBUTE)) {
            return currentElement;
        }

        currentElement = currentElement.parentElement;
    }

    return null;
}

/**
 * Some editors, including Apple Notes, encode title and heading hierarchy as
 * plain block text with different font sizes instead of semantic `<h*>` tags.
 * Collect the distinct block font sizes in the pasted fragment so we can infer
 * that hierarchy relative to the rest of the selection.
 */
function collectRelativeHeadingCandidateFontSizes({
    parsingRoot,
    requiresBoldWeight,
}: {
    parsingRoot: HTMLElement,
    requiresBoldWeight: boolean,
}): number[] {
    const candidateBlocks = Array
        .from(parsingRoot.querySelectorAll<HTMLElement>('*'))
        .filter((element) => {
            const tagName = element.tagName.toLowerCase();
            if (
                tagName === 'blockquote'
                || tagName === 'li'
                || tagName === 'ol'
                || tagName === 'ul'
            ) {
                return false;
            }

            return isBlockElement(element) && elementContainsVisibleText(element);
        });

    return candidateBlocks.reduce<number[]>((acc, element) => {
        const maxFontWeight = resolveElementMaxFontWeight(element);
        const maxFontSizePx = resolveElementMaxFontSizePx(element);
        if (
            (requiresBoldWeight && maxFontWeight < 600)
            || maxFontSizePx < MIN_RELATIVE_HEADING_FONT_SIZE_PX
        ) {
            return acc;
        }

        if (acc.some((knownFontSizePx) => fontSizesApproximatelyMatch(knownFontSizePx, maxFontSizePx))) {
            return acc;
        }

        acc.push(maxFontSizePx);
        acc.sort((firstSizePx, secondSizePx) => secondSizePx - firstSizePx);
        return acc;
    }, []);
}

/**
 * When a pasted fragment exposes a clear descending hierarchy of bold block
 * font sizes, map the largest size to Header 1 and the next size to Header 2.
 * When the fragment only exposes one bold heading tier, still promote that tier
 * to Header 1 if it is also the fragment's largest visible block size. This
 * preserves Apple Notes-style title-only pastes that would otherwise degrade to
 * Header 2 because the absolute font-size fallback is too conservative.
 */
function resolveHeadingLevelFromRelativeFontHierarchy(element: HTMLElement): HEADER_SIZE | null {
    const parsingRoot = getClipboardParsingRoot(element);
    if (!parsingRoot) {
        return null;
    }

    const currentMaxFontWeight = resolveElementMaxFontWeight(element);
    const currentMaxFontSizePx = resolveElementMaxFontSizePx(element);
    if (
        currentMaxFontWeight < 600
            || currentMaxFontSizePx < MIN_RELATIVE_HEADING_FONT_SIZE_PX
    ) {
        return null;
    }

    const candidateFontSizesPx = collectRelativeHeadingCandidateFontSizes({
        parsingRoot,
        requiresBoldWeight: true,
    });
    if (candidateFontSizesPx.length < 2) {
        if (
            candidateFontSizesPx.length !== 1
            || !fontSizesApproximatelyMatch(currentMaxFontSizePx, candidateFontSizesPx[0])
        ) {
            return null;
        }

        const visibleBlockFontSizesPx = collectRelativeHeadingCandidateFontSizes({
            parsingRoot,
            requiresBoldWeight: false,
        });
        const largestVisibleBlockFontSizePx = visibleBlockFontSizesPx[0];
        const hasSmallerVisibleBlockSize = visibleBlockFontSizesPx.some((fontSizePx) => (
            !fontSizesApproximatelyMatch(fontSizePx, largestVisibleBlockFontSizePx)
        ));
        if (
            largestVisibleBlockFontSizePx
            && fontSizesApproximatelyMatch(currentMaxFontSizePx, largestVisibleBlockFontSizePx)
            && hasSmallerVisibleBlockSize
        ) {
            return HEADER_SIZE.one;
        }

        return null;
    }

    if (fontSizesApproximatelyMatch(currentMaxFontSizePx, candidateFontSizesPx[0])) {
        return HEADER_SIZE.one;
    }

    if (fontSizesApproximatelyMatch(currentMaxFontSizePx, candidateFontSizesPx[1])) {
        return HEADER_SIZE.two;
    }

    return null;
}

function resolveHeadingLevelFromMetadata(element: HTMLElement): HEADER_SIZE | null {
    const role = (element.getAttribute('role') || '').trim().toLowerCase();
    if (role === 'heading') {
        const ariaLevel = Number.parseInt(element.getAttribute('aria-level') || '', 10);
        if (ariaLevel === HEADER_SIZE.one) {
            return HEADER_SIZE.one;
        }

        if (ariaLevel === HEADER_SIZE.two) {
            return HEADER_SIZE.two;
        }
    }

    const rawStyle = (element.getAttribute('style') || '').toLowerCase();
    const outlineLevelMatch = rawStyle.match(/\b(?:mso-)?outline-level\s*:\s*([1-6])\b/);
    if (outlineLevelMatch) {
        const outlineLevel = Number.parseInt(outlineLevelMatch[1], 10);
        if (outlineLevel === HEADER_SIZE.one) {
            return HEADER_SIZE.one;
        }

        if (outlineLevel === HEADER_SIZE.two) {
            return HEADER_SIZE.two;
        }
    }

    const className = typeof element.className === 'string'
        ? element.className
        : element.getAttribute('class') || '';
    if (/\bmsoheading1\b|\bheading[-_\s]?1\b/i.test(className)) {
        return HEADER_SIZE.one;
    }

    if (/\bmsoheading2\b|\bheading[-_\s]?2\b/i.test(className)) {
        return HEADER_SIZE.two;
    }

    return null;
}

function resolveHeadingLevel(element: HTMLElement): HEADER_SIZE | null {
    const tagName = element.tagName.toLowerCase();
    if (tagName === 'h1') {
        return HEADER_SIZE.one;
    }

    if (tagName === 'h2') {
        return HEADER_SIZE.two;
    }

    const metadataHeadingLevel = resolveHeadingLevelFromMetadata(element);
    if (metadataHeadingLevel) {
        return metadataHeadingLevel;
    }

    const relativeHeadingLevel = resolveHeadingLevelFromRelativeFontHierarchy(element);
    if (relativeHeadingLevel) {
        return relativeHeadingLevel;
    }

    const maxFontSizePx = resolveElementMaxFontSizePx(element);
    const maxFontWeight = resolveElementMaxFontWeight(element);

    const boldEnoughForHeading = maxFontWeight >= 600;
    if (maxFontSizePx >= HEADER_ONE_MIN_FONT_SIZE_PX && boldEnoughForHeading) {
        return HEADER_SIZE.one;
    }

    if (maxFontSizePx >= HEADER_TWO_MIN_FONT_SIZE_PX && boldEnoughForHeading) {
        return HEADER_SIZE.two;
    }

    return null;
}

function cloneMarks(marks: IClipboardMarks): IClipboardMarks {
    return {
        [EMPHASIZER_MARK.bold]: marks[EMPHASIZER_MARK.bold],
        [EMPHASIZER_MARK.italicize]: marks[EMPHASIZER_MARK.italicize],
        [EMPHASIZER_MARK.underline]: marks[EMPHASIZER_MARK.underline],
        [EMPHASIZER_MARK.strikethrough]: marks[EMPHASIZER_MARK.strikethrough],
    };
}

function resolveActiveMarks({
    element,
    activeMarks,
}: {
    element: HTMLElement,
    activeMarks: IClipboardMarks,
}): IClipboardMarks {
    const nextMarks = cloneMarks(activeMarks);
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'b' || tagName === 'strong') {
        nextMarks[EMPHASIZER_MARK.bold] = true;
    }

    if (tagName === 'em' || tagName === 'i') {
        nextMarks[EMPHASIZER_MARK.italicize] = true;
    }

    if (tagName === 'u') {
        nextMarks[EMPHASIZER_MARK.underline] = true;
    }

    if (tagName === 'del' || tagName === 's' || tagName === 'strike') {
        nextMarks[EMPHASIZER_MARK.strikethrough] = true;
    }

    const fontWeight = getResolvedStyleValue({
        element,
        inlineValue: element.style.fontWeight,
        pickComputedValue: (style) => style.fontWeight,
    }).toLowerCase();
    const numericFontWeight = Number(fontWeight);
    if (
        fontWeight === 'bold'
        || fontWeight === 'bolder'
        || (!Number.isNaN(numericFontWeight) && numericFontWeight >= 600)
    ) {
        nextMarks[EMPHASIZER_MARK.bold] = true;
    }

    const fontStyle = getResolvedStyleValue({
        element,
        inlineValue: element.style.fontStyle,
        pickComputedValue: (style) => style.fontStyle,
    }).toLowerCase();
    if (fontStyle.includes('italic') || fontStyle.includes('oblique')) {
        nextMarks[EMPHASIZER_MARK.italicize] = true;
    }

    const textDecoration = `${getResolvedStyleValue({
        element,
        inlineValue: element.style.textDecorationLine,
        pickComputedValue: (style) => style.textDecorationLine,
    })} ${getResolvedStyleValue({
        element,
        inlineValue: element.style.textDecoration,
        pickComputedValue: (style) => style.textDecoration,
    })}`
        .toLowerCase()
        .trim();
    if (
        textDecoration.includes('underline')
        && !shouldIgnoreImplicitLinkUnderline({
            element,
            textDecoration,
        })
    ) {
        nextMarks[EMPHASIZER_MARK.underline] = true;
    }

    if (textDecoration.includes('line-through')) {
        nextMarks[EMPHASIZER_MARK.strikethrough] = true;
    }

    return nextMarks;
}

function buildTextLeaf(text: string, marks: IClipboardMarks): CustomText | null {
    if (text.length === 0) {
        return null;
    }

    const nextText: CustomText = {
        text,
    };

    if (marks[EMPHASIZER_MARK.bold]) {
        nextText[EMPHASIZER_MARK.bold] = true;
    }

    if (marks[EMPHASIZER_MARK.italicize]) {
        nextText[EMPHASIZER_MARK.italicize] = true;
    }

    if (marks[EMPHASIZER_MARK.underline]) {
        nextText[EMPHASIZER_MARK.underline] = true;
    }

    if (marks[EMPHASIZER_MARK.strikethrough]) {
        nextText[EMPHASIZER_MARK.strikethrough] = true;
    }

    return nextText;
}

function getTextNodeMarkSignature(node: CustomText): string {
    return [
        !!node[EMPHASIZER_MARK.bold],
        !!node[EMPHASIZER_MARK.italicize],
        !!node[EMPHASIZER_MARK.underline],
        !!node[EMPHASIZER_MARK.strikethrough],
    ].join('|');
}

function mergeAdjacentInlineTextNodes(children: Descendant[]): Descendant[] {
    return children.reduce<Descendant[]>((acc, child) => {
        if (!SlateText.isText(child)) {
            acc.push(child);
            return acc;
        }

        const previousChild = acc[acc.length - 1];
        if (
            previousChild
            && SlateText.isText(previousChild)
            && getTextNodeMarkSignature(previousChild) === getTextNodeMarkSignature(child)
        ) {
            previousChild.text += child.text;
            return acc;
        }

        acc.push({
            ...child,
        });
        return acc;
    }, []);
}

function trimInlineEdgeWhitespace(children: Descendant[]): Descendant[] {
    if (children.length === 0) {
        return children;
    }

    const nextChildren = [...children];
    const firstChild = nextChildren[0];
    if (firstChild && SlateText.isText(firstChild)) {
        nextChildren[0] = {
            ...firstChild,
            text: firstChild.text.replace(/^\s+/, ''),
        };
    }

    const lastIndex = nextChildren.length - 1;
    const lastChild = nextChildren[lastIndex];
    if (lastChild && SlateText.isText(lastChild)) {
        nextChildren[lastIndex] = {
            ...lastChild,
            text: lastChild.text.replace(/\s+$/, ''),
        };
    }

    return nextChildren.filter((child) => {
        if (!SlateText.isText(child)) {
            return true;
        }

        return child.text.length > 0;
    });
}

function sanitizeInlineChildren(children: Descendant[]): Descendant[] {
    return trimInlineEdgeWhitespace(mergeAdjacentInlineTextNodes(children));
}

function pushLineBreakToken(target: Array<Descendant | IClipboardLineBreakToken>): void {
    target.push(LINE_BREAK_TOKEN);
}

/**
 * Clipboard providers often encode pasted line breaks inside a text node
 * rather than as `<br>` elements. Split those literal newline characters into
 * explicit Slate line-break tokens so write mode preserves the author’s lines.
 */
function deserializeTextNode({
    node,
    activeMarks,
}: {
    node: globalThis.Text,
    activeMarks: IClipboardMarks,
}): Array<Descendant | IClipboardLineBreakToken> {
    const normalizedNodeText = normalizeTextNewlines(node.textContent || '');
    if (!normalizedNodeText.includes('\n')) {
        const normalizedText = normalizeTextLineSegment(normalizedNodeText);
        const textLeaf = buildTextLeaf(normalizedText, activeMarks);
        return textLeaf ? [textLeaf] : [];
    }

    const nodeTextSegments = normalizedNodeText.split('\n');

    return nodeTextSegments.reduce<Array<Descendant | IClipboardLineBreakToken>>((acc, currentSegment, index) => {
        const normalizedSegment = normalizeTextLineSegment(currentSegment);
        const textLeaf = buildTextLeaf(normalizedSegment, activeMarks);
        if (textLeaf) {
            acc.push(textLeaf);
        }

        if (index === nodeTextSegments.length - 1) {
            return acc;
        }

        const hasContentBeforeBreak = nodeTextSegments
            .slice(0, index + 1)
            .some((segment) => segmentContainsVisibleText(segment));
        const hasContentAfterBreak = nodeTextSegments
            .slice(index + 1)
            .some((segment) => segmentContainsVisibleText(segment));

        if (hasContentBeforeBreak && hasContentAfterBreak) {
            pushLineBreakToken(acc);
        }

        return acc;
    }, []);
}

/**
 * Flattens HTML descendants into the inline node shapes Slate accepts inside
 * line-like blocks. Nested block wrappers become line breaks instead of raw
 * unsupported elements.
 */
function deserializeInlineChildren({
    parentNode,
    activeMarks,
}: IDeserializeInlineChildrenArgs): Array<Descendant | IClipboardLineBreakToken> {
    return Array.from(parentNode.childNodes).reduce<Array<Descendant | IClipboardLineBreakToken>>((acc, childNode) => {
        if (isElementNode(childNode) && shouldIgnoreElement(childNode)) {
            return acc;
        }

        if (isElementNode(childNode) && childNode.tagName.toLowerCase() === 'br') {
            pushLineBreakToken(acc);
            return acc;
        }

        if (isElementNode(childNode) && isBlockElement(childNode)) {
            const nextMarks = resolveActiveMarks({
                element: childNode,
                activeMarks,
            });
            const flattenedBlockChildren = deserializeInlineChildren({
                parentNode: childNode,
                activeMarks: nextMarks,
            });

            if (flattenedBlockChildren.length === 0) {
                return acc;
            }

            if (
                acc.length > 0
                && INLINE_BLOCK_BOUNDARY_TAG_NAMES.has(childNode.tagName.toLowerCase())
            ) {
                pushLineBreakToken(acc);
            }

            acc.push(...flattenedBlockChildren);

            if (INLINE_BLOCK_BOUNDARY_TAG_NAMES.has(childNode.tagName.toLowerCase())) {
                pushLineBreakToken(acc);
            }
            return acc;
        }

        acc.push(...deserializeInlineNode({
            node: childNode,
            activeMarks,
        }));
        return acc;
    }, []);
}

function deserializeInlineNode({
    node,
    activeMarks,
}: IDeserializeInlineNodeArgs): Array<Descendant | IClipboardLineBreakToken> {
    if (isTextNode(node)) {
        return deserializeTextNode({
            node,
            activeMarks,
        });
    }

    if (!isElementNode(node) || shouldIgnoreElement(node)) {
        return [];
    }

    const nextMarks = resolveActiveMarks({
        element: node,
        activeMarks,
    });

    if (node.tagName.toLowerCase() === 'a') {
        const inlineChildren = sanitizeInlineChildren(
            deserializeInlineChildren({
                parentNode: node,
                activeMarks: nextMarks,
            }).filter((child): child is Descendant => !isLineBreakToken(child)),
        );

        if (inlineChildren.length === 0) {
            return [];
        }

        const href = (node.getAttribute('href') || '').trim();
        if (href.length === 0) {
            return inlineChildren;
        }

        const linkNode: WebLinkNode = {
            id: uuidv4(),
            type: 'web-link',
            href,
            children: inlineChildren,
        };

        return [linkNode];
    }

    return deserializeInlineChildren({
        parentNode: node,
        activeMarks: nextMarks,
    });
}

function buildLineNode(children: Descendant[]): LineNode {
    const lineNode = LineElement.newLineElement();
    lineNode.children = children.length > 0 ? children : [{ text: '' }];
    return lineNode;
}

function stripBoldMarksFromChildren(children: Descendant[]): Descendant[] {
    return children.map((child) => {
        if (SlateText.isText(child)) {
            const remainingChild = { ...child };
            delete remainingChild[EMPHASIZER_MARK.bold];
            return remainingChild;
        }

        if (!('children' in child) || !Array.isArray(child.children)) {
            return child;
        }

        return {
            ...child,
            children: stripBoldMarksFromChildren(child.children as Descendant[]),
        };
    });
}

function buildHeaderNode(headerSize: HEADER_SIZE, children: Descendant[]): HeaderNode {
    const headerNode = HeaderElement.newHeaderElement({
        headerSize,
    });
    headerNode.children = children.length > 0
        ? stripBoldMarksFromChildren(children)
        : [{ text: '' }];
    return headerNode;
}

function buildBlockQuoteNode(children: Descendant[]): BlockQuoteNode {
    const blockQuoteNode = BlockQuoteElement.newBlockQuoteElement();
    blockQuoteNode.children = children.length > 0 ? children : [{ text: '' }];
    return blockQuoteNode;
}

function buildBlocksFromInlineNodes({
    inlineNodes,
    buildBlock,
    allowEmptyBreaks = false,
}: IBuildBlocksFromInlineNodesArgs): Descendant[] {
    const blocks: Descendant[] = [];
    let currentInlineChildren: Descendant[] = [];

    const flushCurrentInlineChildren = ({
        allowEmpty = false,
    }: {
        allowEmpty?: boolean,
    } = {}): void => {
        const sanitizedChildren = sanitizeInlineChildren(currentInlineChildren);
        currentInlineChildren = [];

        if (sanitizedChildren.length === 0) {
            if (allowEmpty) {
                blocks.push(buildBlock([{ text: '' }]));
            }
            return;
        }

        blocks.push(buildBlock(sanitizedChildren));
    };

    inlineNodes.forEach((child) => {
        if (isLineBreakToken(child)) {
            flushCurrentInlineChildren({
                allowEmpty: allowEmptyBreaks && blocks.length > 0,
            });
            return;
        }

        currentInlineChildren.push(child);
    });

    flushCurrentInlineChildren();
    return blocks;
}

function buildLineLikeBlocks({
    element,
    activeMarks,
    buildBlock,
    allowEmptyBreaks = false,
}: IBuildLineLikeBlocksArgs): Descendant[] {
    const blocks = buildBlocksFromInlineNodes({
        inlineNodes: deserializeInlineChildren({
            parentNode: element,
            activeMarks: resolveActiveMarks({
                element,
                activeMarks,
            }),
        }),
        buildBlock,
        allowEmptyBreaks,
    });

    if (blocks.length > 0) {
        return blocks;
    }

    /**
     * Several clipboard providers encode intentional blank lines as empty block
     * elements or `<br>`-only paragraphs instead of literal `\n` text nodes.
     * Preserve those blocks as empty Slate lines so spacing survives paste.
     */
    const normalizedBlockText = normalizeTextLineSegment(
        normalizeTextNewlines(element.textContent || ''),
    ).trim();
    if (normalizedBlockText.length > 0) {
        return blocks;
    }

    const blockContainsNonTextMedia = !!element.querySelector(
        'audio, canvas, iframe, img, object, picture, svg, table, video',
    );
    if (blockContainsNonTextMedia) {
        return blocks;
    }

    const blockChildrenOnlyRepresentSpacing = Array
        .from(element.childNodes)
        .every((childNode) => nodeOnlyRepresentsSpacing(childNode));
    if (!blockChildrenOnlyRepresentSpacing) {
        return blocks;
    }

    return [buildBlock([{ text: '' }])];
}

function deserializeListItemElement({
    element,
    activeMarks,
}: {
    element: HTMLElement,
    activeMarks: IClipboardMarks,
}): ListItemNode | null {
    const lineChildren = sanitizeInlineChildren(
        deserializeInlineChildren({
            parentNode: element,
            activeMarks: resolveActiveMarks({
                element,
                activeMarks,
            }),
        }).filter((child): child is Descendant => !isLineBreakToken(child)),
    );

    if (lineChildren.length === 0) {
        return null;
    }

    const listItemNode = ListItemElement.newListItemElement();
    listItemNode.children = [buildLineNode(lineChildren)];
    return listItemNode;
}

function deserializeListElement({
    element,
    activeMarks,
}: {
    element: HTMLElement,
    activeMarks: IClipboardMarks,
}): ListNode | null {
    const listType = element.tagName.toLowerCase() === 'ol'
        ? LIST_TYPE.ordered
        : LIST_TYPE.unordered;
    const listNode = ListElement.newListElement(listType);

    listNode.children = Array.from(element.children).reduce<ListItemNode[]>((acc, childNode) => {
        if (!isElementNode(childNode) || childNode.tagName.toLowerCase() !== 'li') {
            return acc;
        }

        const listItemNode = deserializeListItemElement({
            element: childNode,
            activeMarks,
        });
        if (!listItemNode) {
            return acc;
        }

        acc.push(listItemNode);
        return acc;
    }, []);

    if (listNode.children.length === 0) {
        return null;
    }

    return listNode;
}

/**
 * Converts supported pasted HTML blocks into the editor's Slate schema while
 * gracefully flattening unsupported wrappers back into paragraphs.
 */
function deserializeBlockElement({
    element,
    activeMarks,
}: {
    element: HTMLElement,
    activeMarks: IClipboardMarks,
}): Descendant[] {
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'ol' || tagName === 'ul') {
        const listNode = deserializeListElement({
            element,
            activeMarks,
        });

        return listNode ? [listNode] : [];
    }

    const headingLevel = resolveHeadingLevel(element);
    if (headingLevel === HEADER_SIZE.one) {
        return buildLineLikeBlocks({
            element,
            activeMarks,
            buildBlock: (children) => buildHeaderNode(HEADER_SIZE.one, children),
        });
    }

    if (headingLevel === HEADER_SIZE.two) {
        return buildLineLikeBlocks({
            element,
            activeMarks,
            buildBlock: (children) => buildHeaderNode(HEADER_SIZE.two, children),
        });
    }

    if (tagName === 'blockquote') {
        return buildLineLikeBlocks({
            element,
            activeMarks,
            buildBlock: (children) => buildBlockQuoteNode(children),
        });
    }

    if (
        tagName === 'p'
        || tagName === 'div'
        || tagName === 'article'
        || tagName === 'section'
        || tagName === 'header'
        || tagName === 'footer'
        || tagName === 'aside'
        || tagName === 'main'
        || tagName === 'h3'
        || tagName === 'h4'
        || tagName === 'h5'
        || tagName === 'h6'
    ) {
        return buildLineLikeBlocks({
            element,
            activeMarks,
            buildBlock: (children) => buildLineNode(children),
            allowEmptyBreaks: true,
        });
    }

    return deserializeBlockChildren({
        parentNode: element,
        activeMarks: resolveActiveMarks({
            element,
            activeMarks,
        }),
    });
}

function deserializeBlockChildren({
    parentNode,
    activeMarks,
}: {
    parentNode: ParentNode,
    activeMarks: IClipboardMarks,
}): Descendant[] {
    const blocks: Descendant[] = [];
    let bufferedInlineNodes: Array<Descendant | IClipboardLineBreakToken> = [];

    const flushBufferedInlineNodes = (): void => {
        if (bufferedInlineNodes.length === 0) {
            return;
        }

        blocks.push(...buildBlocksFromInlineNodes({
            inlineNodes: bufferedInlineNodes,
            buildBlock: (children) => buildLineNode(children),
        }));
        bufferedInlineNodes = [];
    };

    Array.from(parentNode.childNodes).forEach((childNode) => {
        if (isElementNode(childNode) && shouldIgnoreElement(childNode)) {
            return;
        }

        if (isElementNode(childNode) && childNode.tagName.toLowerCase() === 'br') {
            pushLineBreakToken(bufferedInlineNodes);
            return;
        }

        if (isElementNode(childNode) && isBlockElement(childNode)) {
            flushBufferedInlineNodes();
            blocks.push(...deserializeBlockElement({
                element: childNode,
                activeMarks,
            }));
            return;
        }

        bufferedInlineNodes.push(...deserializeInlineNode({
            node: childNode,
            activeMarks,
        }));
    });

    flushBufferedInlineNodes();

    return blocks;
}

function deserializeSlateClipboardHtml({
    html,
}: IDeserializeSlateClipboardHtmlArgs): Descendant[] | null {
    if (!html.trim()) {
        return null;
    }

    if (
        typeof document !== 'undefined'
        && !!document.body
    ) {
        const parsingRoot = document.createElement('div');
        parsingRoot.setAttribute(CLIPBOARD_PARSING_ROOT_ATTRIBUTE, 'true');
        parsingRoot.setAttribute('aria-hidden', 'true');
        parsingRoot.style.position = 'fixed';
        parsingRoot.style.left = '-100000px';
        parsingRoot.style.top = '0';
        parsingRoot.style.width = '1px';
        parsingRoot.style.height = '1px';
        parsingRoot.style.opacity = '0';
        parsingRoot.style.pointerEvents = 'none';
        parsingRoot.style.overflow = 'hidden';
        parsingRoot.innerHTML = html;
        document.body.appendChild(parsingRoot);

        try {
            const children = deserializeBlockChildren({
                parentNode: parsingRoot,
                activeMarks: {},
            });

            return children.length > 0 ? children : null;
        } finally {
            parsingRoot.remove();
        }
    }

    if (typeof DOMParser === 'undefined') {
        return null;
    }

    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, 'text/html');
    documentFragment.body.setAttribute(CLIPBOARD_PARSING_ROOT_ATTRIBUTE, 'true');
    const children = deserializeBlockChildren({
        parentNode: documentFragment.body,
        activeMarks: {},
    });

    return children.length > 0 ? children : null;
}

export default deserializeSlateClipboardHtml;
